import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';

import { Pagination, TableSortLabel } from '@mui/material';
import PropTypes from 'prop-types';
import TablePagination from '@mui/material/TablePagination';
import Box from '@mui/material/Box';

/**
 *
 * @param headers is a list of header names (strings)
 * @param rows is a list of AvlTableCell objects lists.
 *
 * type AvlTableCell {
 *      align: string,
 *      style: {},
 *      data: {},
 * }
 * @param pageCount is a total count of pages
 * @param page is an index of the current page
 * @param onPageChange is a callback function on page change event
 * @param onRequestSort is a callback function on sort event
 * @param onChangeRowsPerPage is a callback function on rows per page change event
 * @param rowsPerPage is a number of rows per page amount
 * @param rowsCount is a number of rows amount
 * @param orderBy is an id of sorting column header
 * @param order is one of ['asc', 'desc'] options
 * @param isSortEnabled is a boolean value. Set true, if you want to show sort icons in headers.
 * @param isRowsPerPageOptionsVisible is a boolean value. Set true, if you want to show rows per page indicator.
 *  False by default
 * @param isPaginationDisabled is a boolean value. Set true, if you want to hide pagination buttons.
 *  False by default
 * @param rowsPerPageOptions is a list of options of rows per page indicator
 */
export function AvlTable({
    headers,
    rows,
    pageCount,
    page,
    onPageChange,
    onRequestSort,
    onChangeRowsPerPage,
    rowsPerPage,
    rowsCount,
    orderBy,
    order,
    isSortEnabled = false,
    isRowsPerPageOptionsVisible = false,
    isPaginationDisabled = false,
    rowsPerPageOptions = [10, 20, 30, 50, 100],
}) {
    const handleChangeRowsPerPage = (event) => {
        const rowsPerPage = parseInt(event.target.value, 10);
        onChangeRowsPerPage(rowsPerPage);
    };

    const normalizedHandlerPageChange = (event, page) => {
        onPageChange(event, page + 1);
    };

    const handleSorting = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {
                                headers.map((header, headerIndex) => (
                                    <TableCell
                                        key={headerIndex}
                                        align={header.align}
                                    >
                                        {
                                            isSortEnabled && header.isSortEnabled
                                                ? (
                                                    <TableSortLabel
                                                        active={orderBy === header.id}
                                                        direction={orderBy === header.id ? order : 'asc'}
                                                        onClick={handleSorting(header.id)}
                                                    >
                                                        {header.label}
                                                        {orderBy === header.id ? (
                                                            <Box component="span" sx={visuallyHidden}>
                                                                {order === 'desc'
                                                                    ? 'sorted descending'
                                                                    : 'sorted ascending'}
                                                            </Box>
                                                        ) : null}
                                                    </TableSortLabel>
                                                )
                                                :  header.label
                                        }
                                    </TableCell>
                                ))
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            rows.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {
                                        row.cells.map((cell, cellIndex) => (
                                            <TableCell
                                                key={`${rowIndex}:${cellIndex}`}
                                                align={cell.align}
                                                style={cell.style}
                                            >
                                                {
                                                    row.isCellWrapped
                                                        ? (
                                                            <div
                                                                style={{
                                                                    minHeight: '42px',
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    flexDirection: 'column',
                                                                }}
                                                            >
                                                                {cell.data}
                                                            </div>
                                                        )
                                                        : cell.data
                                                }
                                            </TableCell>
                                        ))
                                    }
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>
            {!isPaginationDisabled && (
                <div style={{ marginTop: '20px' }}>
                    {isRowsPerPageOptionsVisible
                        ? (
                            <TablePagination
                                showFirstButton
                                showLastButton
                                component="div"
                                rowsPerPageOptions={rowsPerPageOptions}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                onPageChange={normalizedHandlerPageChange}
                                count={rowsCount}
                                page={page - 1}
                            />
                        ) : (
                            <Pagination
                                showFirstButton
                                showLastButton
                                count={pageCount}
                                page={page}
                                siblingCount={1}
                                boundaryCount={1}
                                variant="outlined"
                                shape="rounded"
                                onChange={onPageChange}
                            />
                        )}
                </div>
            )}
        </>
    );
}

Table.propTypes = {
    headers: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
    pageCount: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
};
